/**
 * /web/api/profile/* React Query hooks.
 *
 * The change-password mutation forces sign-out: the backend's
 * `change_password` service calls `revoke_user_sessions`, which deletes
 * every active SID for the user — including the one the SPA is using.
 * Callers must navigate to /login on success.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ApiError } from "./client";

export interface IpEntry {
  ip: string;
  added: string | null;
}

interface IpListResponse {
  items: IpEntry[];
}

export interface SessionEntry {
  sid_short: string;
  is_current: boolean;
  ip_at_login: string;
  created_at: number;
  last_seen: number;
}

interface SessionListResponse {
  items: SessionEntry[];
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

interface ChangePasswordResponse {
  status: string;
  message: string;
}

// ---- change password -----------------------------------------------------

export function useChangePassword() {
  const qc = useQueryClient();
  return useMutation<ChangePasswordResponse, ApiError, ChangePasswordRequest>({
    mutationFn: (body) =>
      api.post<ChangePasswordResponse>("/web/api/profile/change_password", {
        body,
        // Backend kills our session as part of the password change — the
        // 401 that follows is expected, not a bug.
        noRedirectOn401: true,
      }),
    onSettled: () => {
      qc.removeQueries({ queryKey: ["me"] });
    },
  });
}

// ---- ip whitelist --------------------------------------------------------

export function useIpWhitelist() {
  return useQuery<IpListResponse, ApiError>({
    queryKey: ["profile", "ip_whitelist"],
    queryFn: () => api.get<IpListResponse>("/web/api/profile/ip_whitelist"),
    staleTime: 30_000,
  });
}

export function useAddIp() {
  const qc = useQueryClient();
  return useMutation<IpListResponse, ApiError, { ip: string }>({
    mutationFn: (body) =>
      api.post<IpListResponse>("/web/api/profile/ip_whitelist", { body }),
    onSuccess: (data) => {
      qc.setQueryData(["profile", "ip_whitelist"], data);
    },
  });
}

export function useRemoveIp() {
  const qc = useQueryClient();
  return useMutation<IpListResponse, ApiError, { ip: string }>({
    mutationFn: ({ ip }) =>
      api.delete<IpListResponse>(
        `/web/api/profile/ip_whitelist?ip=${encodeURIComponent(ip)}`,
      ),
    onSuccess: (data) => {
      qc.setQueryData(["profile", "ip_whitelist"], data);
    },
  });
}

// ---- sessions ------------------------------------------------------------

export function useSessions() {
  return useQuery<SessionListResponse, ApiError>({
    queryKey: ["profile", "sessions"],
    queryFn: () => api.get<SessionListResponse>("/web/api/profile/sessions"),
    staleTime: 15_000,
  });
}

// ---- web ip whitelist toggle --------------------------------------------

export interface WebIpToggleBody {
  enabled: boolean;
  current_password: string;
  code?: string;
}

export function useToggleWebIpWhitelist() {
  const qc = useQueryClient();
  return useMutation<{ enabled: boolean }, ApiError, WebIpToggleBody>({
    mutationFn: (body) =>
      api.post<{ enabled: boolean }>(
        "/web/api/profile/web_ip_whitelist",
        { body },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}
